import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Check, ClipboardList, ArrowRight, ArrowLeft, X } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const STEPS = ["Proje Detayları", "İletişim Bilgileri", "Özet"];

export default function QuoteWizard({ open, onClose, initialItems = [], initialSource = "wizard", initialNotes = "" }) {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    company: "",
    facility_type: "Otel",
    star_target: "4",
    room_count: 0,
    notes: initialNotes,
    items: initialItems,
    source: initialSource,
  });

  useEffect(() => {
    if (open) {
      setStep(0);
      setForm((f) => ({ ...f, items: initialItems, notes: initialNotes || f.notes, source: initialSource }));
    }
    // Only re-run when `open` toggles — initialItems/initialNotes/initialSource are
    // read at that moment and intentionally excluded to prevent render loops
    // from parent re-renders passing fresh default arrays/strings.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canNext = () => {
    if (step === 0) return form.facility_type && form.star_target;
    if (step === 1) return form.contact_name && form.contact_email;
    return true;
  };

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/quotes", {
        ...form,
        room_count: Number(form.room_count || 0),
      });
      toast.success("Teklif talebiniz alındı. 24 saat içinde dönüş yapacağız.");
      onClose();
    } catch (e) {
      toast.error("Gönderim başarısız. Lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden" data-testid="quote-wizard-modal">
        <div className="bg-navy text-white px-7 py-5 flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-gold/20 text-gold flex items-center justify-center">
            <ClipboardList size={20} />
          </div>
          <div className="flex-1">
            <DialogHeader>
              <DialogTitle className="heading-serif text-xl text-white">Teklif Sihirbazı</DialogTitle>
            </DialogHeader>
            <p className="text-white/60 text-xs mt-0.5">
              Otel projeniz için uzman danışmanlığımızla 24 saatte özelleştirilmiş teklif.
            </p>
          </div>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 px-7 pt-5">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center flex-1 gap-2">
              <div
                className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold transition-all ${
                  i < step
                    ? "bg-teal text-white"
                    : i === step
                    ? "bg-gold text-white"
                    : "bg-softbg text-gray-400"
                }`}
              >
                {i < step ? <Check size={14} /> : i + 1}
              </div>
              <span className={`text-xs ${i === step ? "text-navy font-medium" : "text-gray-400"} flex-1`}>{s}</span>
            </div>
          ))}
        </div>

        <div className="p-7 max-h-[60vh] overflow-y-auto admin-scroll">
          {step === 0 && (
            <div className="space-y-4" data-testid="wizard-step-1">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs mb-1 block">Tesis Türü</Label>
                  <select
                    className="w-full border rounded-md h-10 px-3 bg-softbg text-sm"
                    value={form.facility_type}
                    onChange={(e) => update("facility_type", e.target.value)}
                    data-testid="wizard-facility-type"
                  >
                    <option>Otel</option>
                    <option>Butik Otel</option>
                    <option>Resort</option>
                    <option>Apart</option>
                    <option>Pansiyon</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Yıldız Hedefi</Label>
                  <select
                    className="w-full border rounded-md h-10 px-3 bg-softbg text-sm"
                    value={form.star_target}
                    onChange={(e) => update("star_target", e.target.value)}
                    data-testid="wizard-star-target"
                  >
                    {[1,2,3,4,5].map((s) => <option key={s} value={s}>{s} Yıldız</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-xs mb-1 block">Toplam Oda Sayısı</Label>
                <Input
                  type="number"
                  value={form.room_count}
                  onChange={(e) => update("room_count", e.target.value)}
                  data-testid="wizard-room-count"
                />
              </div>
              <div>
                <Label className="text-xs mb-1 block">Proje Notları</Label>
                <Textarea
                  rows={3}
                  placeholder="Projenizle ilgili spesifik talepler, teslimat süreci, bütçe beklentisi..."
                  value={form.notes}
                  onChange={(e) => update("notes", e.target.value)}
                  data-testid="wizard-notes"
                />
              </div>
              {form.items?.length > 0 && (
                <div className="bg-softbg rounded-md p-3 text-xs">
                  <div className="font-medium text-navy mb-2">Eklenen Ürünler ({form.items.length})</div>
                  <ul className="space-y-1">
                    {form.items.map((it, i) => (
                      <li key={i} className="flex justify-between">
                        <span>{it.name}</span>
                        <span className="text-gray-500">× {it.quantity || 1}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4" data-testid="wizard-step-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs mb-1 block">Ad Soyad *</Label>
                  <Input value={form.contact_name} onChange={(e) => update("contact_name", e.target.value)} data-testid="wizard-name" />
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Firma</Label>
                  <Input value={form.company} onChange={(e) => update("company", e.target.value)} data-testid="wizard-company" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs mb-1 block">E-posta *</Label>
                  <Input type="email" value={form.contact_email} onChange={(e) => update("contact_email", e.target.value)} data-testid="wizard-email" />
                </div>
                <div>
                  <Label className="text-xs mb-1 block">Telefon</Label>
                  <Input value={form.contact_phone} onChange={(e) => update("contact_phone", e.target.value)} data-testid="wizard-phone" />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3 text-sm" data-testid="wizard-step-3">
              <div className="bg-softbg rounded-lg p-5 space-y-2">
                <Row label="Ad Soyad" v={form.contact_name} />
                <Row label="E-posta" v={form.contact_email} />
                <Row label="Telefon" v={form.contact_phone || "-"} />
                <Row label="Firma" v={form.company || "-"} />
                <div className="border-t my-2" />
                <Row label="Tesis Türü" v={form.facility_type} />
                <Row label="Yıldız Hedefi" v={`${form.star_target} Yıldız`} />
                <Row label="Oda Sayısı" v={String(form.room_count || 0)} />
                <Row label="Kaynak" v={form.source} />
                {form.notes && <Row label="Notlar" v={form.notes} />}
              </div>
              {form.items?.length > 0 && (
                <div className="bg-white border rounded-lg p-5">
                  <div className="font-medium text-navy mb-2">Ürünler ({form.items.length})</div>
                  <ul className="text-xs space-y-1.5 max-h-48 overflow-y-auto admin-scroll">
                    {form.items.map((it, i) => (
                      <li key={i} className="flex justify-between gap-2">
                        <span className="flex-1">{it.name}</span>
                        <span className="text-gray-500">× {it.quantity || 1}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="text-xs text-gray-500">
                Talebinizi gönderdiğinizde, danışman ekibimiz 24 saat içinde size özel bir teklifle dönüş yapar.
              </p>
            </div>
          )}
        </div>

        <div className="border-t bg-softbg px-7 py-4 flex items-center justify-between">
          {step > 0 ? (
            <Button variant="ghost" onClick={() => setStep((s) => s - 1)} data-testid="wizard-back">
              <ArrowLeft size={14} className="mr-1" /> Geri
            </Button>
          ) : (
            <Button variant="ghost" onClick={onClose} data-testid="wizard-close">
              <X size={14} className="mr-1" /> Vazgeç
            </Button>
          )}
          {step < 2 ? (
            <Button onClick={() => setStep((s) => s + 1)} disabled={!canNext()} className="bg-gold hover:bg-[#c49d58]" data-testid="wizard-next">
              Devam <ArrowRight size={14} className="ml-1" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={loading} className="bg-gold hover:bg-[#c49d58]" data-testid="wizard-submit">
              {loading ? "Gönderiliyor..." : "Teklif Talebini Gönder"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Row = ({ label, v }) => (
  <div className="flex justify-between gap-3 text-xs">
    <span className="text-gray-500">{label}</span>
    <span className="font-medium text-navy text-right">{v}</span>
  </div>
);
